import { useCallback, useContext, useEffect } from "react";

import { clearLocalRole, getLocalRole, setLocalRole } from "clientData";
import { userBelongsToCompany } from "utils";

import { roleContext } from "../context";
import { useAuthContext } from "./useAuthContext";

export function useRoleContext() {
  const roleCtx = useContext(roleContext);
  const { user } = useAuthContext();

  const getAnyRole = useCallback(() => {
    if (user != null) {
      const foundRole = user.roles.find(
        (role: Role) => role.type === "CARRIER" || role.type === "SHIPPER",
      );
      if (!foundRole) {
        return null;
      }
      return foundRole;
    }
    return null;
  }, [user]);

  const refreshRole = useCallback(() => {
    if (getAnyRole() != null) {
      roleCtx.dispatch({ type: "setRole", role: getAnyRole() as Role });
      setLocalRole(getAnyRole() as Role);
    }
  }, [getAnyRole, roleCtx]);

  function getAdminRole(): Role | null {
    if (user != null) {
      const foundRole = user.roles.find(
        (role: Role) => role.type === "ADMINISTRATOR",
      );
      if (foundRole) {
        return foundRole;
      }
    }
    return null;
  }

  function listBranchAdminRoles(): Role[] {
    if (user != null) {
      const foundRoles = user.roles.filter(
        (role: Role) => role.type === "BRANCH_ADMINISTRATOR",
      );
      if (foundRoles.length > 0) {
        return foundRoles;
      }
    }
    return [];
  }

  function hasBranchAdminRole(): boolean {
    const roles = listBranchAdminRoles();
    return roles && roles.length > 0;
  }

  function hasCompanyAdminRole(): boolean {
    const admin = getAdminRole();
    return admin != null;
  }

  function getBranchAdminRole(branchId: number): Role | null {
    if (user != null) {
      const foundRole = user.roles.find(
        (role: Role) =>
          role.type === "BRANCH_ADMINISTRATOR" && role.branch === branchId,
      );
      if (foundRole) {
        return foundRole;
      }
    }
    return null;
  }

  useEffect(() => {
    if (user == null) {
      roleCtx.dispatch({ type: "clearRole" });
      clearLocalRole();
    } else if (roleCtx.role == null) {
      const localRole = getLocalRole();
      if (localRole == null) {
        if (getAnyRole() != null) {
          roleCtx.dispatch({ type: "setRole", role: getAnyRole() as Role });
          setLocalRole(getAnyRole() as Role);
        } else if (roleCtx.isLoading) {
          // user has no role — loading is complete, there is no company
          roleCtx.dispatch({ type: "setLoaded" });
        }
      } else if (user.roles.some((role: Role) => role.id === localRole.id)) {
        roleCtx.dispatch({ type: "setRole", role: localRole });
      } else if (getAnyRole() != null) {
        // stored role belongs to a previous session, not this user
        roleCtx.dispatch({ type: "setRole", role: getAnyRole() as Role });
        setLocalRole(getAnyRole() as Role);
      } else {
        clearLocalRole();
        if (roleCtx.isLoading) {
          roleCtx.dispatch({ type: "setLoaded" });
        }
      }
    } else if (roleCtx.role?.company === null && userBelongsToCompany(user)) {
      refreshRole();
    }
  }, [user, getAnyRole, refreshRole, roleCtx]);
  return {
    role: roleCtx.role,
    company: roleCtx.company,
    isLoading: roleCtx.isLoading,
    getAdminRole,
    listBranchAdminRoles,
    getBranchAdminRole,
    hasBranchAdminRole,
    hasCompanyAdminRole,
  };
}
